(() => {
  const tagName = 'b2h-scroll-rail';
  if (!('customElements' in window) || customElements.get(tagName)) return;

  class B2HScrollRail extends HTMLElement {
    constructor() {
      super();
      this.isConnectedToRail = false;
      this.track = null;
      this.previousButton = null;
      this.nextButton = null;
      this.progressBar = null;
      this.navigation = null;
      this.navigationDisplay = '';
      this.resizeObserver = null;
      this.handleScroll = this.refresh.bind(this);
      this.handleResize = this.refresh.bind(this);
      this.handlePrevious = this.scroll.bind(this, -1);
      this.handleNext = this.scroll.bind(this, 1);
    }

    connectedCallback() {
      if (this.isConnectedToRail) return;

      this.track = this.querySelector(this.dataset.trackSelector || '[data-scroll-rail-track]');
      if (!this.track) return;

      this.previousButton = this.querySelector(this.dataset.previousSelector || '[data-scroll-rail-previous]');
      this.nextButton = this.querySelector(this.dataset.nextSelector || '[data-scroll-rail-next]');
      this.progressBar = this.querySelector(this.dataset.progressSelector || '[data-scroll-rail-progress]');
      this.navigation = this.querySelector(this.dataset.navigationSelector || '[data-scroll-rail-navigation]');
      this.navigationDisplay = this.navigation ? this.navigation.style.display : '';
      this.isConnectedToRail = true;

      if (!this.style.display) this.style.display = 'block';
      this.track.addEventListener('scroll', this.handleScroll, { passive: true });
      if (this.previousButton) this.previousButton.addEventListener('click', this.handlePrevious);
      if (this.nextButton) this.nextButton.addEventListener('click', this.handleNext);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.handleResize, { passive: true });
      }

      requestAnimationFrame(this.handleResize);
    }

    disconnectedCallback() {
      if (!this.isConnectedToRail || !this.track) return;

      this.track.removeEventListener('scroll', this.handleScroll);
      if (this.previousButton) this.previousButton.removeEventListener('click', this.handlePrevious);
      if (this.nextButton) this.nextButton.removeEventListener('click', this.handleNext);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      else window.removeEventListener('resize', this.handleResize);

      this.resizeObserver = null;
      this.isConnectedToRail = false;
    }

    getStep() {
      const cardSelector = this.dataset.cardSelector;
      const card = cardSelector ? this.track.querySelector(cardSelector) : this.track.firstElementChild;
      const fallbackWidth = Number.parseFloat(this.dataset.fallbackCardWidth) || this.track.clientWidth * 0.8;
      if (!card) return fallbackWidth;

      const trackStyle = getComputedStyle(this.track);
      const gap = Number.parseFloat(trackStyle.columnGap || trackStyle.gap) || 0;
      return card.getBoundingClientRect().width + gap;
    }

    scroll(direction) {
      if (!this.track) return;
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollBy({
        left: direction * this.getStep() * 2,
        behavior: prefersReducedMotion ? 'auto' : 'smooth'
      });
    }

    refresh() {
      if (!this.track) return;

      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const hasOverflow = maxScroll > 1;
      if (this.navigation) {
        this.navigation.style.display = hasOverflow
          ? (this.dataset.navigationDisplay || this.navigationDisplay)
          : 'none';
      }

      const scrollLeft = Math.max(0, this.track.scrollLeft);
      if (this.previousButton) this.previousButton.disabled = !hasOverflow || scrollLeft <= 1;
      if (this.nextButton) this.nextButton.disabled = !hasOverflow || scrollLeft >= maxScroll - 1;

      if (!this.progressBar) return;
      if (!hasOverflow || this.track.scrollWidth === 0) {
        this.progressBar.style.width = '100%';
        this.progressBar.style.transform = 'translateX(0)';
        return;
      }

      const ratio = Math.min(1, this.track.clientWidth / this.track.scrollWidth);
      const offset = scrollLeft / this.track.scrollWidth;
      this.progressBar.style.width = `${ratio * 100}%`;
      this.progressBar.style.transform = `translateX(${(offset / ratio) * 100}%)`;
    }
  }

  customElements.define(tagName, B2HScrollRail);
})();
